// PRD 17 §3.8 — 「SMS·이메일 발신 전체(참고)」 inventory shown at the bottom of the read-only
// Notification Policy screen. Two recipient-scoped tables (환자가 받는 발신 / 의료진이 받는 발신),
// 4 columns each (발신 / 채널 / 시점·트리거 / 구분), served Korean-only by the backend.
//
// Independent of the policy table above: its own query + states, so a reference-load failure
// shows an inline retry here and never breaks the policy list (SHCT-427 AC-04).

import { useTranslation } from 'react-i18next';

import { useSendingReferenceQuery } from '../hooks/use-notification-settings';
import type { SendingReferenceRow } from '../types/notification-settings.types';

function ReferenceTable({
  heading,
  rows,
  note,
}: {
  heading: string;
  rows: SendingReferenceRow[];
  note: string;
}) {
  const { t } = useTranslation('notification-settings');
  return (
    <div>
      <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>{heading}</h3>
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="table" style={{ width: '100%' }}>
          <thead>
            {/* SHCT-700 — the other header table on this page; QC asked for the same one-line
                treatment here. Headers only: the body cells keep wrapping. */}
            <tr>
              <th style={{ width: '28%', whiteSpace: 'nowrap' }}>{t('reference.columns.sender')}</th>
              <th style={{ width: '18%', whiteSpace: 'nowrap' }}>{t('reference.columns.channel')}</th>
              <th style={{ whiteSpace: 'nowrap' }}>{t('reference.columns.timing')}</th>
              <th style={{ width: '15%', whiteSpace: 'nowrap' }}>{t('reference.columns.category')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row.sender}-${i}`}>
                <td>{row.sender}</td>
                <td>{row.channel}</td>
                <td style={{ color: 'var(--muted-foreground)' }}>{row.timing}</td>
                <td>{row.category}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: '8px 0 0' }}>{note}</p>
    </div>
  );
}

export function SendingReferenceSection() {
  const { t } = useTranslation('notification-settings');
  const { data, isLoading, error, refetch } = useSendingReferenceQuery();

  return (
    <section style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>📋 {t('reference.title')}</h2>

      {isLoading ? (
        <div
          className="card animate-pulse"
          style={{ height: 120, background: 'var(--muted)', borderRadius: 'var(--radius-sm)' }}
        />
      ) : error || !data ? (
        <div
          role="alert"
          style={{
            padding: 16,
            border: '1px solid var(--destructive)',
            borderRadius: 'var(--radius-md)',
            background: '#FEE2E8',
            color: 'var(--destructive)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <span>{t('reference.loadError')}</span>
          <button type="button" className="btn btn-sm btn-outline" onClick={() => void refetch()}>
            {t('states.retry')}
          </button>
        </div>
      ) : (
        <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 24 }}>
          <ReferenceTable
            heading={`👤 ${t('reference.patientGroup')}`}
            rows={data.patientReceives}
            note={t('reference.patientNote')}
          />
          <ReferenceTable
            heading={`🩺 ${t('reference.staffGroup')}`}
            rows={data.staffReceives}
            note={t('reference.staffNote')}
          />
        </div>
      )}
    </section>
  );
}
